import * as React from 'react';

export interface UseMutationObserverArgs {
  ref: React.RefObject<HTMLElement>;
  // same as MutationCallback, but MutationObserver is nullable
  callback: (element: HTMLElement, mutations: MutationRecord[], observer: MutationObserver | null) => void;
  observerOptions: {
    attributes?: boolean;
    characterData?: boolean;
    childList?: boolean;
    subtree?: boolean;
  };
}
export type UseMutationObserverType = (args: UseMutationObserverArgs) => void;

/**
 * A hook that uses the MutationObserver API to observe changes to a DOM element.
 * Differently from the regular MutationObserver, this hook also runs the callback
 * on first mount to make sure the initial state is correct. If you need this effect
 * to only run after the first mutation, you can add a check in the callback.
 *
 * This hook only runs the callback when the ref is not null and the element is in the DOM.
 *
 * @param element The element to observe
 * @param callback The callback to run when a mutation is observed
 * @param observerOptions The options to pass to the MutationObserver
 */
const useMutationObserver: UseMutationObserverType = options => {
  const { ref, callback, observerOptions } = options;

  const wrappedCallback: MutationCallback = React.useCallback(
    (mutations, observer) => {
      if (!ref.current) {
        return;
      }
      callback(ref.current, mutations, observer);
    },
    [callback, ref],
  );

  // Run the callback on mount
  React.useEffect(() => {
    if (!ref.current) {
      return;
    }
    callback(ref.current, [], null);
  }, [callback, ref, wrappedCallback]);

  // MutationObserver set up effect
  React.useEffect(() => {
    if (!ref.current) {
      return;
    }
    const observer = new MutationObserver(wrappedCallback);
    observer.observe(ref.current, observerOptions);

    return () => observer.disconnect();
  }, [callback, observerOptions, ref, wrappedCallback]);
};

export default useMutationObserver;
